import { cva, type VariantProps } from "class-variance-authority";
import cx from "classnames";
import { HelperText } from "../HelperText/HelperText";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { Info } from "components/icons/Info";
import { SmallTriangleWarning } from "components/icons/SmallTriangleWarning";
import React, {
  ComponentProps,
  InputHTMLAttributes,
  ReactNode,
  useId,
} from "react";
import BankWarningCircle from "static/images/BankWarningCircle.svg";
import CheckCircle from "static/images/CheckCircle.svg";
import SearchLoadingIcon from "static/images/SearchLoadingIcon.svg";
import { SwitchCase } from "../SwitchCase/SwitchCase";
import { Label } from "../TextInput/TextInput";

export type InputSize = NonNullable<
  VariantProps<typeof wrapperVariants>["size"]
>;
export type InputVariant = NonNullable<
  VariantProps<typeof wrapperVariants>["variant"]
>;

export interface BareInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  helperText?: string;
  customSize?: InputSize;
  infoText?: ReactNode;
  leftComponent?: React.ReactNode;
  rightComponent?: React.ReactNode;
  showLeftSeparator?: boolean;
  required?: boolean;
  variant?: InputVariant;
  infoTextSide?: ComponentProps<typeof ConditionalToolTip>["side"];
}

export const wrapperVariants = cva(
  "t-flex t-items-center t-w-full t-overflow-hidden t-border t-border-solid t-rounded-md t-transition-colors t-duration-200 focus-within:!t-border-purple-50 t-text-text-30 data-[disabled]:t-bg-neutral-0 data-[disabled]:t-text-neutral-40",
  {
    variants: {
      variant: {
        default:
          "t-border-neutral-10 hover:t-border-neutral-20 data-[disabled]:hover:t-border-neutral-10",
        error: "t-border-red-50",
        success:
          "t-border-neutral-10 hover:t-border-neutral-20 data-[disabled]:hover:t-border-neutral-10",
        warning:
          "t-border-neutral-10 hover:t-border-neutral-20 data-[disabled]:hover:t-border-neutral-10",
        loading: "t-border-neutral-10",
      },
      size: {
        small: "t-h-8",
        regular: "t-h-10",
        large: "t-h-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "regular",
    },
  }
);

export const inputFieldVariants = cva(
  "t-flex-1 t-min-w-0 t-h-full t-border-none t-outline-none t-appearance-none t-shadow-none t-bg-neutral-66 disabled:t-bg-neutral-0 t-pl-3 t-pr-3 t-text-neutral-100 placeholder:t-text-neutral-40 disabled:t-text-text-30 disabled:placeholder:t-text-neutral-20 disabled:t-cursor-not-allowed",
  {
    variants: {
      size: {
        small: "t-py-1.5 t-text-body-sm",
        regular: "t-py-2 t-text-body-sm",
        large: "t-py-3 t-text-body-sm",
      },
      hasRightIcon: {
        true: "t-pr-0",
        false: "t-pr-3",
      },
    },
    defaultVariants: {
      size: "regular",
      hasRightIcon: false,
    },
  }
);

export const textSizeVariants = cva("", {
  variants: {
    size: {
      small: "t-text-body-sm",
      regular: "t-text-body-sm",
      large: "t-text-body-lg",
    },
  },
  defaultVariants: { size: "regular" },
});

export const flagSizeVariants = cva("t-rounded-full t-object-cover t-block", {
  variants: {
    size: {
      small: "t-w-3 t-h-3",
      regular: "t-w-4 t-h-4",
      large: "t-w-4 t-h-4",
    },
  },
  defaultVariants: { size: "regular" },
});

const iconSizeVariants = cva("t-mx-2 t-pointer-events-none", {
  variants: {
    size: {
      small: "t-size-3",
      regular: "t-size-4",
      large: "t-size-4",
    },
  },
  defaultVariants: { size: "regular" },
});

export const InputStatusIcon = ({
  size,
  variant,
}: {
  size: InputSize;
  variant: InputVariant;
}) => {
  return (
    <SwitchCase
      value={variant}
      cases={{
        error: (
          <img
            src={BankWarningCircle}
            alt="Error"
            className={iconSizeVariants({ size })}
          />
        ),
        warning: (
          <SmallTriangleWarning
            className={cx(iconSizeVariants({ size }), "t-text-orange-50")}
          />
        ),
        success: (
          <img
            src={CheckCircle}
            alt="Success"
            className={iconSizeVariants({ size })}
          />
        ),
        loading: (
          <img
            src={SearchLoadingIcon}
            alt="Loading"
            className={iconSizeVariants({ size })}
          />
        ),
      }}
      default={null}
    />
  );
};

const BareInput = React.forwardRef<HTMLInputElement, BareInputProps>(
  (
    {
      label,
      helperText,
      customSize = "regular",
      leftComponent,
      rightComponent,
      showLeftSeparator = true,
      required = false,
      variant = "default",
      className,
      disabled,
      infoText,
      infoTextSide = "right",
      ...props
    },
    ref
  ) => {
    const id = useId();
    const hasRightIcon = !rightComponent;

    return (
      <div className="t-flex t-flex-col t-w-full">
        {label && (
          <div className="t-flex t-items-center t-gap-1 t-mb-1">
            <Label
              htmlFor={props.name || id}
              required={required}
              className="!t-pb-0"
            >
              {label}
            </Label>
            {infoText && (
              <ConditionalToolTip
                condition={infoText}
                side={infoTextSide}
                propClass="t-max-w-[500px]"
              >
                <Info color="currentColor" className="t-text-text-30" />
              </ConditionalToolTip>
            )}
          </div>
        )}

        <div
          data-disabled={disabled ? "" : undefined}
          className={wrapperVariants({ variant, size: customSize })}
        >
          {leftComponent && (
            <div className="t-flex-none t-flex t-self-stretch t-items-center t-pl-3 t-gap-3">
              <div
                className={cx(
                  "t-flex t-self-stretch t-items-center",
                  textSizeVariants({ size: customSize })
                )}
              >
                {leftComponent}
              </div>
              {showLeftSeparator && (
                <span className="t-text-neutral-20">|</span>
              )}
            </div>
          )}

          <input
            id={props.name || props.id || id}
            ref={ref}
            name={props.name}
            required={required}
            disabled={disabled}
            className={cx(
              inputFieldVariants({ size: customSize, hasRightIcon }),
              className
            )}
            {...props}
          />

          {rightComponent && (
            <div
              className={cx(
                "t-flex-none t-flex t-self-stretch t-items-center t-pr-3",
                textSizeVariants({ size: customSize })
              )}
            >
              {rightComponent}
            </div>
          )}
          <InputStatusIcon size={customSize} variant={variant} />
        </div>

        {helperText && <HelperText variant={variant}>{helperText}</HelperText>}
      </div>
    );
  }
);

BareInput.displayName = "BareInput";

export default BareInput;
